import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button, Divider, Spin, Tabs } from '@arco-design/web-react';
import { IconArrowLeft } from '@arco-design/web-react/icon';

import { Version } from '@/components';
import { clearUserSessionStorage, setAccessToken, setLoginConfigInfo, setRefeshToken } from '@/utils/useLocalStorage';
import useUrlParams from '@/utils/useUrlParams';

import styles from '../index.module.less';
import { getLoginType } from '../service';
import LoginExtra from './extra';
import LDAP from './ldap';
import LoginLogo from './logo';
import Register from './register';
import Standard from './standard';

const TabPane = Tabs.TabPane;

// 注册方式
const REGISTER_FLAG = ['phoneRegister', 'emailRegister'] as const;

const tabNavRowStyle: React.CSSProperties = {
  display: 'flex',
  marginTop: '16px',
  justifyContent: 'space-between',
};

const textBtnStyle: React.CSSProperties = { padding: 0 };

type LoginConfig = Record<string, boolean | undefined>;

export default function Login({ logoURL }: { logoURL?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('login');
  const [loginConfig, setLoginConfig] = useState<LoginConfig>({});
  const [loading, setLoading] = useState(false);
  const { getParam } = useUrlParams();

  useEffect(() => {
    setLoading(true);
    getLoginType()
      .then((data: LoginConfig) => {
        if (data) {
          setLoginConfig(data);
          setLoginConfigInfo(data);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const goHome = useCallback(() => navigate('/home'), [navigate]);
  const registerTypes = useMemo(() => REGISTER_FLAG.filter((flag) => loginConfig[flag]), [loginConfig]);

  const spinProps = {
    className: styles.content,
    block: true,
    loading,
    style: { marginBottom: '2rem' } as React.CSSProperties,
  };

  const applyLoginResult = useCallback((res: { accessToken?: string; refreshToken?: string }) => {
    if (!res || Object.keys(res).length === 0) return false;
    // 提交登录成功后再清旧会话，避免登录页挂载时误伤其他标签页。
    clearUserSessionStorage();
    if (res.accessToken) setAccessToken(res.accessToken);
    if (res.refreshToken) setRefeshToken(res.refreshToken);
    return true;
  }, []);

  const onSuccess = useCallback(
    (res: { accessToken?: string; refreshToken?: string }) => {
      if (!applyLoginResult(res ?? {})) return;
      const prePath = decodeURIComponent(getParam('redirect', '/home'));
      if (prePath.startsWith('http')) {
        window.location.href = prePath;
      } else {
        navigate(prePath, { replace: true });
      }
    },
    [navigate, getParam, applyLoginResult],
  );

  if (activeTab === 'ldap') {
    return (
      <Spin {...spinProps}>
        <LoginLogo onClick={goHome} style={{ marginBottom: '2rem' }} src={logoURL} />
        <LDAP onSuccess={onSuccess} />
        <div style={tabNavRowStyle}>
          <Button type="text" size="small" style={textBtnStyle} onClick={() => setActiveTab('login')}>
            <IconArrowLeft /> {t('actions:back')}
          </Button>
          {registerTypes.length > 0 && (
            <Button type="text" size="small" style={textBtnStyle} onClick={() => setActiveTab('register')}>
              {t('login-page:register')}
            </Button>
          )}
        </div>
      </Spin>
    );
  }

  return (
    <div>
      <LoginLogo onClick={goHome} src={logoURL} />
      <div style={{ padding: '0 18px' }}>
        <Divider />
      </div>
      <Spin {...spinProps}>
        <Tabs activeTab={activeTab} onChange={setActiveTab} type="text" size="large">
          <TabPane key="login" title={t('login-page:login')}>
            <Standard onSuccess={onSuccess} />
            <Version />
          </TabPane>
          {registerTypes.length > 0 && (
            <TabPane key="register" title={t('login-page:register')}>
              <Register loginConfig={loginConfig} registerTypes={registerTypes} setActiveTab={setActiveTab} />
            </TabPane>
          )}
        </Tabs>
      </Spin>
      {activeTab === 'login' && loginConfig?.ldap && <LoginExtra setActiveTab={setActiveTab} />}
    </div>
  );
}
